import { useEffect, useState } from 'react';
import { useUser } from '@supabase/auth-helpers-react';
import { useAuthStore } from '@/store/auth.store';
import { supabase } from '@/lib/supabase';

export const useTutorialPreferences = () => {
  const supabaseUser = useUser();
  const { user: storeUser } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [tutorialCompleted, setTutorialCompleted] = useState(false);

  const user = supabaseUser || storeUser;

  useEffect(() => {
    const fetchTutorialPreferences = async () => {
      console.log('Current user state:', {
        supabaseUser,
        storeUser,
        user,
        userId: user?.id
      });
      
      if (!user?.id) {
        console.log('No user ID found, skipping fetch');
        setIsLoading(false);
        return;
      }

      try {
        // Primeiro, tenta buscar as preferências existentes
        const { data: existingPrefs, error: fetchError } = await supabase
          .from('user_preferences')
          .select('*')
          .eq('user_id', user.id)
          .single();

        console.log('Fetch result:', { existingPrefs, fetchError });

        if (fetchError && fetchError.code === 'PGRST116') {
          // Registro não encontrado, vamos criar um novo
          console.log('Creating new user preferences for user:', user.id);
          const { data: newPrefs, error: insertError } = await supabase
            .from('user_preferences')
            .insert([{
              user_id: user.id,
              tutorial_completed: false,
              theme: 'light',
              notifications_enabled: true,
              email_notifications: true,
              language: 'pt-BR',
              updated_at: new Date().toISOString(),
            }])
            .select('*')
            .single();

          if (insertError) {
            console.error('Error creating preferences:', insertError);
            throw insertError;
          }
          
          console.log('New preferences created:', newPrefs);
          setTutorialCompleted(false);
        } else if (fetchError) {
          console.error('Error fetching preferences:', fetchError);
          throw fetchError;
        } else {
          console.log('Existing preferences found:', existingPrefs);
          setTutorialCompleted(existingPrefs?.tutorial_completed ?? false);
        }
      } catch (error: any) {
        console.error('Erro ao carregar preferências do tutorial:', error);
        console.error('Detalhes do erro:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        setTutorialCompleted(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTutorialPreferences();
  }, [user, supabaseUser, storeUser]);

  const updateTutorialCompleted = async (completed: boolean) => {
    console.log('Updating tutorial completed:', {
      completed,
      user,
      userId: user?.id
    });
    
    if (!user?.id) {
      console.error('No user ID found, cannot update preferences');
      throw new Error('Usuário não encontrado. Por favor, faça login novamente.');
    }

    try {
      // Primeiro, verifica se já existe um registro
      const { data: existingPrefs } = await supabase
        .from('user_preferences')
        .select('id')
        .eq('user_id', user.id)
        .single();

      let updateOperation;
      if (existingPrefs?.id) {
        // Se existe, atualiza
        console.log('Updating existing preferences');
        updateOperation = supabase
          .from('user_preferences')
          .update({
            tutorial_completed: completed,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', user.id);
      } else {
        // Se não existe, insere
        console.log('Creating new preferences');
        updateOperation = supabase
          .from('user_preferences')
          .insert([{
            user_id: user.id,
            tutorial_completed: completed,
            theme: 'light',
            notifications_enabled: true,
            email_notifications: true,
            language: 'pt-BR',
            updated_at: new Date().toISOString(),
          }]);
      }

      const { error } = await updateOperation;

      if (error) {
        console.error('Error updating preferences:', error);
        console.error('Detalhes do erro:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      console.log('Tutorial preferences updated successfully');
      setTutorialCompleted(completed);
    } catch (error: any) {
      console.error('Erro ao atualizar status do tutorial:', error);
      console.error('Detalhes do erro:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      throw error;
    }
  };

  return {
    isLoading,
    tutorialCompleted,
    updateTutorialCompleted,
  };
}; 